import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  DETAIL_PANE_ACTIONS_SLOT_ID,
  DETAIL_PANE_HEADER_SLOT_ID,
  PAGE_HEADER_ACTIONS_SLOT_ID,
  PAGE_HEADER_BREADCRUMB_SLOT_ID,
  PAGE_HEADER_EXTRA_SLOT_ID,
} from '@/lib/page-header'

function usePortalSlot(slotId: string): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setSlot(document.getElementById(slotId))
  }, [slotId])
  return slot
}

/**
 * Renders its children into the shell's header action slot. Used by
 * pages that declare `staticData.pageHeader` and need dynamic buttons
 * (create/upload/etc.) in the header bar without owning the chrome.
 */
export function PageHeaderActions({ children }: { children: ReactNode }) {
  const slot = usePortalSlot(PAGE_HEADER_ACTIONS_SLOT_ID)
  return slot ? createPortal(children, slot) : null
}

/** Breadcrumb rendered to the left of the page title. */
export function PageHeaderBreadcrumb({ children }: { children: ReactNode }) {
  const slot = usePortalSlot(PAGE_HEADER_BREADCRUMB_SLOT_ID)
  return slot ? createPortal(children, slot) : null
}

/** Inline content rendered immediately after the page title. */
export function PageHeaderExtra({ children }: { children: ReactNode }) {
  const slot = usePortalSlot(PAGE_HEADER_EXTRA_SLOT_ID)
  return slot ? createPortal(children, slot) : null
}

/**
 * Renders into the Controls detail pane's header strip — the pane's equivalent
 * of the page header, so a Test opened beside the list gets its code and its
 * actions on the strip instead of spending body rows on them.
 */
export function DetailPaneHeader({ children }: { children: ReactNode }) {
  const slot = usePortalSlot(DETAIL_PANE_HEADER_SLOT_ID)
  return slot ? createPortal(children, slot) : null
}

/** Actions rendered on the right side of the Controls detail-pane header. */
export function DetailPaneActions({ children }: { children: ReactNode }) {
  const slot = usePortalSlot(DETAIL_PANE_ACTIONS_SLOT_ID)
  return slot ? createPortal(children, slot) : null
}
